import { Component } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'

import { ConnectedHelpTooltip } from '../../@paco/connectors'
import { getSettingEnabled } from '../../@paco/entities/Setting/SettingHelpers'
import trans from '../../@paco/helpers/trans'
import { approveTrack, getTracks } from '../../@paco/redux/tracks/tracksActions'

import ButtonAuthenticate from '../../components/ButtonAuthenticate/ButtonAuthenticate'
import NewFilters from '../../components/Filters/Filters'
import ItemsList from '../../components/ItemsList/ItemsList'
import SearchBar from '../../components/SearchBar/SearchBar'
import SidebarPage from '../../components/SidebarPage/SidebarPage'
import TrackCardsContainer from '../../components/TrackCardsContainer/TrackCardsContainer'
import { BREAK_MOBILE, TRACK_TYPE_REVIEW } from '../../constants'
import { addHours, getEndOfWorkDay, getStartOfWorkDay } from '../../helpers/date'
import { translate } from '../../helpers/translations/translator'
import { setEmployeeSearch, setFilter } from '../../redux/filter/filterActions'
import { setStartAndEndDate } from '../../redux/weekNavigator/weekNavigatorActions'
import { transformPayrollPeriodToLegacyPayrollPeriod } from '../../services/PayrollPeriodService/transformPayrollPeriodLegacy'
import CalendarToolbar from '../Schedule/components/CalendarToolbar/CalendarToolbar'
import { setActiveTrackFromUrl } from './helpers/setActiveTrackFromUrl'

class ReviewTracks extends Component {
    state = {
        tracks: [],
        exportForm: false,
        activeTrackId: undefined,
        legacyPayrollPeriods: [],
    }

    componentDidMount() {
        const { dispatch } = this.props

        const trackIdFromUrl = dispatch(setActiveTrackFromUrl())
        this.setState({ activeTrackId: trackIdFromUrl })

        this.getTracks()
    }

    componentDidUpdate(prevProps) {
        const activeDepartmentOptions = this.props.departmentOptions.filter(option => option.isChecked)
        const prevActiveDepartmentOptions = prevProps.departmentOptions.filter(option => option.isChecked)

        if (activeDepartmentOptions.length !== prevActiveDepartmentOptions.length) {
            this.getTracks()
        }

        if (this.props.startDate !== prevProps.startDate || this.props.endDate !== prevProps.endDate) {
            this.getTracks()
        }

        if (this.isMobile() && this.props.selectedDate !== prevProps.selectedDate) {
            this.getTracks()
        }

        if (this.props.filter.employeeSearch !== prevProps.filter.employeeSearch) {
            this.getTracks()
        }
    }

    isMobile = () => window.innerWidth <= BREAK_MOBILE

    static getDerivedStateFromProps(props, state) {
        const { tracks, onReviewTrackClick } = props
        const { activeTrackId } = state

        if (props.tracks !== state.tracks) {
            const matchedTrack = tracks.find(track => track.id === activeTrackId)

            if (matchedTrack) {
                onReviewTrackClick(matchedTrack, TRACK_TYPE_REVIEW)
            }
        }

        const activeTrackNotFound = !! (props.tracks.length || state.tracks.length) && ! tracks.some(track => track.id === activeTrackId)
        const legacyPayrollPeriods = props.payrollPeriods.map(transformPayrollPeriodToLegacyPayrollPeriod)

        return {
            ...state,
            legacyPayrollPeriods,
            tracks: props.tracks,
            activeTrackId: activeTrackNotFound ? undefined : state.activeTrackId,
        }
    }

    dispatchSetFilters = (filter) => {
        const { dispatch } = this.props
        dispatch(setFilter(filter, this.getTracks))
    }

    approveTrack = async (track) => {
        const { dispatch } = this.props
        await dispatch(approveTrack(track.id))
    }

    setStartAndEndDate = (selectedDate, startDate, endDate) => {
        const { dispatch } = this.props
        dispatch(setStartAndEndDate(selectedDate, startDate, endDate))
    }

    getTracks = async () => {
        const {
            dispatch,
            startDate,
            endDate,
            selectedDate,
            departmentOptions,
            filter,
        } = this.props

        const isMobile = this.isMobile()
        const effectiveStartDate = isMobile ? selectedDate : startDate
        const effectiveEndDate = isMobile ? selectedDate : endDate

        // Earliest shifts start at 04:00, so we add an extra threshold so we also get the tracks of employees who clock in early.
        const tracksStart = addHours(getStartOfWorkDay(effectiveStartDate), -1)

        dispatch(getTracks(
            TRACK_TYPE_REVIEW,
            {
                start: tracksStart,
                end: getEndOfWorkDay(effectiveEndDate),
            },
            undefined,
            departmentOptions,
            filter.employeeSearch,
            filter.employeeContractTypes,
        ))
    }

    onSearchBarChange = (value) => {
        const { dispatch } = this.props
        dispatch(setEmployeeSearch(value))
    }

    renderTracks() {
        const {
            tracksLoading,
            eventLogsLoading,
            filter,
            pagination,
            permissions,
            settings,
            timeMode,
            currentUser,
            currentUserRole,
            tracksToReview,
            payrollPeriods,
            onReviewTrackClick,
            loadingTrackIds,
        } = this.props
        const { employeeSearch } = filter
        const isTimerEnableFeedback = getSettingEnabled('TIMER_ENABLE_FEEDBACK', settings)

        const sortedTracks = [...tracksToReview].sort((a, b) => new Date(b.checkInOutPeriod.end) - new Date(a.checkInOutPeriod.end))

        return (
            <>
                <div className="tracks-overview-top">
                    <CalendarToolbar
                        timeMode={timeMode}
                        showAllOption={! this.isMobile()}
                        payrollPeriods={payrollPeriods.map(transformPayrollPeriodToLegacyPayrollPeriod)}
                        setStartAndEndDate={this.setStartAndEndDate}
                        afterDateChange={this.afterDateChange}
                    />
                    <ConnectedHelpTooltip
                        index={2}
                        route="review-tracks"
                        subTitle={trans('help.tracks.reviewTracks.title')}
                        text={trans('help.tracks.reviewTracks.text')}
                        title={trans('help.tracks.title')}
                        className="tracks__review-tracks-help-tooltip"
                    />
                    <div className="tracks-overview-top-right">
                        <SearchBar placeholder={translate('common.searchEmployee')} startVal={employeeSearch} onChange={this.onSearchBarChange} />
                        <ConnectedHelpTooltip
                            index={0}
                            route="review-tracks"
                            subTitle={trans('help.tracks.viewOptions.title')}
                            text={trans('help.tracks.viewOptions.text')}
                            title={trans('help.tracks.title')}
                            showMobileInfoWarning
                            className="tracks__view-options-help-tooltip"
                        />
                    </div>
                </div>
                <ItemsList
                    isLoading={tracksLoading || eventLogsLoading}
                    pagination={pagination}
                    itemsLength={sortedTracks.length}
                    onPagination={this.getTracks}
                    emptyMessage={translate('pages.tracks.emptyList')}
                >
                    <TrackCardsContainer
                        isTimerEnableFeedback={isTimerEnableFeedback}
                        tracks={sortedTracks}
                        onReviewTrackClick={onReviewTrackClick}
                        onApproveTrackClick={this.approveTrack}
                        permissions={permissions}
                        currentUserId={currentUser?.id}
                        currentUserRole={currentUserRole}
                        settings={settings}
                        payrollPeriods={payrollPeriods}
                        className="trackcards-container"
                        loadingTrackIds={loadingTrackIds}
                    />
                </ItemsList>
            </>
        )
    }

    render() {
        const {
            filter,
            canAddTracks,
            employmentTypes,
            onAddTrackClick,
        } = this.props

        return (
            <SidebarPage
                sidebarContent={(
                    <NewFilters
                        filter={filter}
                        employmentTypes={employmentTypes}
                        button={canAddTracks && (
                            <ButtonAuthenticate onClick={onAddTrackClick} text={translate('pages.tracks.addTrack')} />
                        )}
                        filterBlocks={[
                            'contract-types',
                        ]}
                        onChange={this.dispatchSetFilters}
                    />
                )}
            >
                <div className="tracks-list tracks-review">
                    {this.renderTracks()}
                </div>
                <ConnectedHelpTooltip
                    index={1}
                    route="review-tracks"
                    subTitle={trans('help.tracks.filters.title')}
                    text={trans('help.tracks.filters.text')}
                    title={trans('help.tracks.title')}
                    showMobileInfoWarning
                    className="tracks__filters-help-tooltip"
                />
            </SidebarPage>
        )
    }
}

ReviewTracks.propTypes = {
    tracksLoading: PropTypes.bool.isRequired,
    eventLogsLoading: PropTypes.bool.isRequired,
    tracksToReview: PropTypes.array.isRequired,
    dispatch: PropTypes.func.isRequired,
    filter: PropTypes.object.isRequired,
    timeMode: PropTypes.string.isRequired,
    startDate: PropTypes.object.isRequired,
    endDate: PropTypes.object.isRequired,
    selectedDate: PropTypes.object.isRequired,
    pagination: PropTypes.object.isRequired,
    onAddTrackClick: PropTypes.func.isRequired,
    onReviewTrackClick: PropTypes.func.isRequired,
    permissions: PropTypes.array.isRequired,
    payrollPeriods: PropTypes.array.isRequired,
    currentUser: PropTypes.object.isRequired,
    employmentTypes: PropTypes.array.isRequired,
    canAddTracks: PropTypes.bool.isRequired,
    loadingTrackIds: PropTypes.array.isRequired,
}

export default connect(null)(ReviewTracks)
